import React from 'react';
import { useTranslation } from 'react-i18next';
import type { QuestionMessage } from '../../types/protocol';
import { TextField } from './TextField';
import { SelectOneField } from './SelectOneField';
import { SelectManyField } from './SelectManyField';
import { NumberField } from './NumberField';
import { BooleanField } from './BooleanField';
import { ScheduleField } from './ScheduleField';
import { GroupField } from './GroupField';
import { NodeConfigField } from './NodeConfigField';
import { FlowBuilderField } from './FlowBuilderField';
import { ReviewDataField } from './ReviewDataField';

interface FieldRendererProps {
  question: QuestionMessage;
  onSubmit: (value: unknown) => void;
  onSearch?: (stepId: string, query: string) => void;
  previewData?: unknown;
}

export function FieldRenderer({ question, onSubmit, onSearch, previewData }: FieldRendererProps): React.ReactElement {
  const { t } = useTranslation();
  switch (question.fieldType) {
    case 'text':
      return (
        <TextField
          field={question.field}
          defaultValue={question.default as string | undefined}
          help={question.help}
          required={question.required}
          onSubmit={onSubmit}
        />
      );

    case 'search':
      return (
        <SelectManyField
          field={question.field}
          options={question.options ?? []}
          defaultValue={question.default as Array<string | number> | undefined}
          help={question.help}
          allowSelectAll={question.allowSelectAll}
          allowCreate={question.allowCreate}
          createLabel={question.createLabel}
          createFields={question.createFields}
          minSelections={question.minSelections}
          onSubmit={onSubmit}
          searchable
        />
      );

    case 'select_one':
      return (
        <SelectOneField
          field={question.field}
          options={question.options ?? []}
          defaultValue={question.default as string | number | undefined}
          help={question.help}
          allowCreate={question.allowCreate}
          searchable={!!question.optionsFrom || !!question.searchTool}
          searchTool={question.searchTool}
          stepId={question.stepId}
          onSearch={onSearch}
          onSubmit={onSubmit}
        />
      );

    case 'select_many':
      return (
        <SelectManyField
          field={question.field}
          options={question.options ?? []}
          defaultValue={question.default as Array<string | number> | undefined}
          help={question.help}
          allowSelectAll={question.allowSelectAll}
          allowCreate={question.allowCreate}
          createLabel={question.createLabel}
          createFields={question.createFields}
          minSelections={question.minSelections}
          searchable={!!question.optionsFrom}
          onSubmit={onSubmit}
        />
      );

    case 'number':
      return (
        <NumberField
          field={question.field}
          defaultValue={question.default as number | undefined}
          help={question.help}
          required={question.required}
          onSubmit={onSubmit}
        />
      );

    case 'boolean':
      return (
        <BooleanField
          field={question.field}
          defaultValue={question.default as boolean | undefined}
          help={question.help}
          onSubmit={onSubmit}
        />
      );

    case 'schedule':
      return (
        <ScheduleField
          field={question.field}
          defaultValue={question.default != null && typeof question.default === 'object'
            ? question.default as { '24_7'?: boolean; weekly?: Array<{ dow: number; start: string; end: string }> }
            : question.default === null
              ? null
              : undefined}
          help={question.help}
          required={question.required}
          onSubmit={onSubmit}
        />
      );

    case 'group':
      if (question.nodeConfig) {
        return (
          <NodeConfigField
            field={question.field}
            children={question.children ?? []}
            help={question.help}
            onSubmit={onSubmit}
          />
        );
      }
      return (
        <GroupField
          field={question.field}
          children={question.children ?? []}
          help={question.help}
          onSubmit={onSubmit}
        />
      );

    case 'confirm':
      if (question.reviewData) {
        return <ReviewDataField data={question.reviewData} message={t('fields.confirmMessage')} onConfirm={() => onSubmit(true)} />;
      }
      return <div className="sp-field-confirm">{t('fields.confirmMessage')}</div>;

    case 'flow_builder':
      return (
        <FlowBuilderField
          field={question.field}
          help={question.help}
          onSubmit={onSubmit}
          previewData={previewData}
        />
      );

    default:
      return (
        <TextField
          field={question.field}
          help={question.help}
          required={question.required}
          onSubmit={onSubmit}
        />
      );
  }
}
